import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, ListChecks } from 'lucide-react';
import { activityFeedAPI } from 'services/swagger-api';
import { getActivityRowUrl } from 'features/activity-feed/utils/activityNavigation';
import { formatInstant } from 'core/utils/dates';
import { useDialog } from 'hooks/useDialog';
import { LeadNotificationDto } from './leadNotifications';
import { branchLabel, readableMessage } from './leadMessage';

/** Contexte de lecture des notifications globales (cf. useNotifications). */
const CONTEXTE_NOTIFICATION_GLOBALE = 1;

interface LeadsArrivalDialogContentProps {
  leads: LeadNotificationDto[];
  /** Accusé réception effectif : retire les leads de la liste portée par le layout. */
  onAcknowledged: (ids: string[]) => void;
}

/**
 * Une carte par lead. La branche porte l'identité de la ligne (un courtier lit
 * « Assurance emprunteur » avant de lire un titre générique répété n fois), le
 * client vient de `context.clientName` et le n° de dossier de `riskIdentifier`.
 */
const LeadCard: React.FC<{ lead: LeadNotificationDto }> = ({ lead }) => {
  const details = lead.details ?? [];
  const dossierUrl = getActivityRowUrl(lead);
  const branch = branchLabel(lead.context?.branchCode);
  const client = lead.context?.clientName || lead.title;
  const message = readableMessage(lead.message, lead.entities);

  return (
    <li className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 leading-tight">
          <div className="flex flex-wrap items-center gap-2">
            {branch && (
              <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                {branch}
              </span>
            )}
            <span className="font-semibold text-gray-900">{client}</span>
          </div>
          {message && <p className="mt-1 text-sm text-gray-600">{message}</p>}
          <p className="mt-1 text-xs text-gray-400">
            {/* createdAt = instant (ISO avec Z) -> fuseau du navigateur */}
            {formatInstant(lead.createdAt)}
            {lead.context?.riskIdentifier && (
              <>
                {' · '}
                <span className="font-mono">{lead.context.riskIdentifier}</span>
              </>
            )}
          </p>
        </div>

        {/* Nouvel onglet : la modale reste ouverte, le courtier peut consulter ses
            dossiers puis revenir accuser réception en une fois. */}
        {dossierUrl && (
          <a
            href={dossierUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-none items-center gap-1.5 rounded-lg border border-primary/30 px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/5"
          >
            Ouvrir le dossier
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      {/* Libellés rédigés par le back, affichés tels quels. `details` existe sur
          toutes les notifications mais reste vide hors leads : pas de bloc si vide. */}
      {details.length > 0 && (
        <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 ring-1 ring-amber-200">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-900">
            <ListChecks className="h-3.5 w-3.5" />
            À compléter avant devis
          </p>
          <ul className="mt-1.5 space-y-1">
            {details.map((detail) => (
              <li key={detail} className="flex items-start gap-2 text-sm text-amber-900">
                <span className="mt-1.5 h-1 w-1 flex-none rounded-full bg-amber-500" />
                {detail}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
};

/**
 * Accueil du courtier : les leads partenaires qui viennent de lui être adressés,
 * avec la checklist d'ajustements telle qu'elle a été présentée au gestionnaire à
 * la conversion (mêmes libellés, `details` de la notification).
 *
 * « J'ai compris » = marquage lu côté back. Fermer sans valider laisse les
 * notifications non lues : la cloche garde son badge et le popup reviendra à la
 * prochaine connexion — c'est voulu, l'information est actionnable.
 */
export const LeadsArrivalDialogContent: React.FC<LeadsArrivalDialogContentProps> = ({
  leads,
  onAcknowledged
}) => {
  const { closeDialog, setDialogActions } = useDialog();
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  const handleAcknowledge = useCallback(async () => {
    setIsAcknowledging(true);
    const ids = leads.map((lead) => lead.id);
    try {
      // Marquage UN PAR UN des leads affichés. Surtout pas notifications/read-all :
      // il viderait la cloche de toutes les catégories d'un coup.
      await Promise.all(
        ids.map((id) =>
          activityFeedAPI.markAsReadAsync({
            id,
            markAsReadCommand: { contexte: CONTEXTE_NOTIFICATION_GLOBALE }
          })
        )
      );
      onAcknowledged(ids);
    } catch (error) {
      // Échec du marquage : on ferme quand même, la cloche reste la source de vérité.
      console.error('Marquage des leads comme lus échoué:', error);
    } finally {
      setIsAcknowledging(false);
      closeDialog();
    }
  }, [leads, onAcknowledged, closeDialog]);

  useEffect(() => {
    setDialogActions({
      submitLabel: isAcknowledging ? 'Enregistrement…' : "J'ai compris",
      submitDisabled: isAcknowledging,
      onSubmit: handleAcknowledge,
      closeLabel: 'Plus tard'
    });
  }, [isAcknowledging, handleAcknowledge, setDialogActions]);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-gray-600">
        {leads.length === 1
          ? 'Le dossier est déjà créé dans votre espace : il ne reste qu’à compléter les informations que le partenaire ne transmet pas, puis à tarifer.'
          : 'Les dossiers sont déjà créés dans votre espace : il ne reste qu’à compléter les informations que le partenaire ne transmet pas, puis à tarifer.'}
      </p>

      {/* Au-delà de 3 leads la liste défile : le pied de modale (« J'ai compris »)
          doit rester visible sans scroller toute la page. */}
      <ul className="flex max-h-[55vh] flex-col gap-3 overflow-y-auto pr-1">
        {leads.map((lead) => (
          <LeadCard key={lead.id} lead={lead} />
        ))}
      </ul>
    </div>
  );
};

export default LeadsArrivalDialogContent;
